<template>
    <card-tab-panel
        name="financial"
        v-bind="$attrs"
        with-variable-height
    >
        <text-heading sub>
            <template v-if="ordersEnabled">
                Transactie
            </template>
            <template v-else>
                Omzet en transactie
            </template>
        </text-heading>

        <flex-container>
            <two-columns class="financial-tab-panel__fields">
                <input-group v-if="!ordersEnabled || !!formData.orderValue" v-model="formData.orderValue">
                    <template #label>
                        <input-label>Totale omzet/courtage</input-label>
                    </template>
                    <template #input>
                        <text-input :disabled="!props.editable">
                            <template #prefix>
                                €
                            </template>
                        </text-input>
                    </template>
                </input-group>

                <input-group v-if="!ordersEnabled || !!formData.commission" v-model="formData.commission">
                    <template #label>
                        <input-label>Commissie</input-label>
                    </template>
                    <template #input>
                        <text-input :disabled="!props.editable">
                            <template #prefix>
                                €
                            </template>
                        </text-input>
                    </template>
                </input-group>

                <input-group v-model="formData.productionDate">
                    <template #label>
                        <input-label>Productiedatum</input-label>
                    </template>
                    <template #input>
                        <date-input clearable :disabled="!props.editable">
                            <template #suffix>
                                <svg-icon :icon="IconCalendarToday" :size="IconSize.sm" />
                            </template>
                        </date-input>
                    </template>
                </input-group>

                <input-group v-model="formData.transportDate">
                    <template #label>
                        <input-label>Transportdatum</input-label>
                    </template>
                    <template #input>
                        <date-input clearable :disabled="!props.editable">
                            <template #suffix>
                                <svg-icon :icon="IconCalendarToday" :size="IconSize.sm" />
                            </template>
                        </date-input>
                    </template>
                </input-group>
            </two-columns>
        </flex-container>

        <flex-container
            v-if="props.editable"
            :gap="Size.sm"
            justify-content="flex-end"
            style="margin-top: 20px"
        >
            <button-outline @click="onCancel">
                Annuleren
            </button-outline>

            <button-block
                :disabled="!hasChanges"
                @loading="onSave"
            >
                Opslaan
            </button-block>
        </flex-container>
    </card-tab-panel>
</template>

<script setup lang="ts">
    import {
        ButtonBlock,
        ButtonOutline,
        CardTabPanel,
        FlexContainer,
        IconSize,
        InputGroup,
        InputLabel,
        Size,
        SvgIcon,
        TextHeading,
        TextInput,
        DateInput,
    } from '@keyvue/one-ui';

    import type { Lead } from '@keyvue/one-api-client';
    import TwoColumns from '@components/UI/TwoColumns.vue';
    import IconCalendarToday from '@keyvue/one-material-icons/icons/calendar_today.svg?component';
    import { computed, ref, watch } from 'vue';
    import useCompanySecurity from '@composables/security/useCompanySecurity';

    export interface Props {
        lead: Lead
        editable?: boolean
    }

    const props = defineProps<Props>();
    const { ordersEnabled } = await useCompanySecurity();

    const formData = ref({
        orderValue: props.lead.transaction.orderValue,
        commission: props.lead.transaction.commission,
        productionDate: props.lead.transaction.productionDate,
        transportDate: props.lead.transaction.transportDate,
    });

    const formApplyInitialValues = () => {
        formData.value = {
            orderValue: props.lead.transaction.orderValue,
            commission: props.lead.transaction.commission,
            productionDate: props.lead.transaction.productionDate,
            transportDate: props.lead.transaction.transportDate,
        };
    };

    const emits = defineEmits(['changed']);
    const hasChanges = computed(() => Object.keys(formData.value).some((key) => formData.value[key] !== props.lead.transaction[key]));

    const onSave = async (event: any) => {
        emits('changed', formData.value);
        event.button.setState('completed');
    };

    const onCancel = async () => {
        formApplyInitialValues();
    };

    watch(() => props.lead.transaction, () => {
        formApplyInitialValues();
    });
</script>

<style scoped lang="scss">
.financial-tab-panel__fields {
    width: 100%;
    line-height: 125%;
}
</style>
